import MetricBox from "components-for/display/metric-box";
import { FunctionComponent, memo, useState } from "react";
import { IInteractionsGetQueryParameters } from "@edgetier/watchtower-types";
import { IProps } from "./interactions-count-metric.types";
import { LIVE_POLL_INTERVAL_MILLISECONDS } from "constants/pages/live";
import { useInteractionQueryParameters } from "hooks-for/filters/use-interaction-query-parameters";
import { useInteractionsCount } from "hooks-for/interactions/use-interactions-counts";
import { useInterval } from "react-use";
import "./interactions-count-metric.scss";

/**
 * Request and display a count of interactions matching some parameters.
 * @param props.queryParameters Interaction query parameters.
 * @param otherProps            Other metric props.
 * @returns                     A metric icon, label, and values.
 */
const InteractionsCountMetric: FunctionComponent<IProps> = ({
    getDynamicParameters,
    pollIntervalMilliseconds = LIVE_POLL_INTERVAL_MILLISECONDS,
    queryParameters,
    ...otherProps
}) => {
    const isAutomaticPoll = typeof getDynamicParameters !== "function";
    const [pollParameters, setPollParameters] = useState<IInteractionsGetQueryParameters>(() =>
        isAutomaticPoll ? {} : getDynamicParameters()
    );

    useInterval(
        () => {
            setPollParameters(isAutomaticPoll ? {} : getDynamicParameters());
        },
        isAutomaticPoll ? null : pollIntervalMilliseconds
    );

    const options = { refetchInterval: isAutomaticPoll ? pollIntervalMilliseconds : undefined };
    const interactionsQueryParameters = useInteractionQueryParameters({ defaults: queryParameters });
    const { data, isLoading } = useInteractionsCount({ ...interactionsQueryParameters, ...pollParameters }, options);
    return <MetricBox {...otherProps} isLoading={isLoading} value={data ?? null} />;
};

export default memo(InteractionsCountMetric);
